import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  LinearProgress,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  People as PeopleIcon,
  Pets as PetsIcon,
  Article as ArticleIcon,
  Store as StoreIcon,
  Inbox as InboxIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Public as PublicIcon,
  Bolt as BoltIcon,
  Park as ParkIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { normalizeImageUrl } from '../services/imageUtils';

// ── Theme tokens ────────────────────────────────────────────────────────────
const C = {
  primary: '#6D28D9',
  primaryLight: '#8B5CF6',
  blue: '#3B82F6',
  green: '#10B981',
  amber: '#F59E0B',
  red: '#EF4444',
  pink: '#EC4899',
  teal: '#14B8A6',
  slate: '#64748B',
  sky: '#0EA5E9',
  gray: '#9CA3AF',
};

const ENERGY_COLOR = {
  low:         C.green,
  moderate:    C.blue,
  high:        C.amber,
  'very high': C.red,
};

const SIZE_COLOR = {
  small:  C.gray,
  medium: C.primaryLight,
  large:  C.sky,
};

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

// ── Reusable card shell ─────────────────────────────────────────────────────
function SectionCard({ title, action, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: '0.68rem',
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Paper>
  );
}

// ── Stat card with delta ────────────────────────────────────────────────────
function StatCard({ label, value, delta, deltaLabel, Icon, color, loading }) {
  const deltaPositive = (delta ?? 0) >= 0;
  const DeltaIcon = deltaPositive ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: '0.68rem',
          }}
        >
          {label}
        </Typography>
        <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 36, height: 36, borderRadius: 1.5 }}>
          <Icon sx={{ fontSize: 18 }} />
        </Avatar>
      </Box>
      {loading ? (
        <Skeleton variant="text" width="60%" sx={{ fontSize: '2rem' }} />
      ) : (
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.1 }}>
          {fmt(value)}
        </Typography>
      )}
      {delta != null && !loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <DeltaIcon sx={{ fontSize: 14, color: deltaPositive ? C.green : C.red }} />
          <Typography variant="caption" sx={{ color: deltaPositive ? C.green : C.red, fontWeight: 600 }}>
            +{fmt(delta)}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {deltaLabel}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ── Pending submissions banner ──────────────────────────────────────────────
function PendingBanner({ pendingCount }) {
  const navigate = useNavigate();
  if (!pendingCount) return null;
  return (
    <Box
      onClick={() => navigate('/submissions')}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        mb: 3,
        borderRadius: 2,
        bgcolor: alpha(C.red, 0.06),
        border: '1px solid',
        borderColor: alpha(C.red, 0.2),
        cursor: 'pointer',
        '&:hover': { bgcolor: alpha(C.red, 0.09) },
      }}
    >
      <Avatar sx={{ bgcolor: alpha(C.red, 0.12), color: C.red, width: 36, height: 36, borderRadius: 1.5 }}>
        <InboxIcon sx={{ fontSize: 18 }} />
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ color: C.red, fontWeight: 600 }}>
          {pendingCount} pending submission{pendingCount !== 1 ? 's' : ''} awaiting review
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Review garden requests, photo suggestions, and provider applications
        </Typography>
      </Box>
      <Chip
        label="Review now"
        size="small"
        sx={{
          bgcolor: C.red,
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.72rem',
          '&:hover': { bgcolor: '#DC2626' },
        }}
      />
    </Box>
  );
}

// ── Top countries list ──────────────────────────────────────────────────────
function TopCountriesList({ items, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={28} />)}
      </Box>
    );
  }
  if (!items || items.length === 0) {
    return <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>No country data yet.</Typography>;
  }
  const max = Math.max(...items.map((i) => i.userCount), 1);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map((item, idx) => {
        const pct = (item.userCount / max) * 100;
        return (
          <Box key={item.country} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 18, fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600, textAlign: 'center' }}>
              {idx + 1}
            </Box>
            <Avatar sx={{ width: 26, height: 26, bgcolor: alpha(C.primary, 0.1), color: C.primary, fontSize: '0.7rem' }}>
              <PublicIcon sx={{ fontSize: 14 }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
                <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1, mr: 1 }}>
                  {item.country}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {fmt(item.userCount)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 5,
                  borderRadius: 3,
                  bgcolor: alpha(C.primary, 0.08),
                  '& .MuiLinearProgress-bar': { bgcolor: C.primary, borderRadius: 3 },
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Top gardens list ────────────────────────────────────────────────────────
function TopGardensList({ items, loading }) {
  const navigate = useNavigate();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={28} />)}
      </Box>
    );
  }
  if (!items || items.length === 0) {
    return <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>No gardens yet.</Typography>;
  }
  const max = Math.max(...items.map((i) => i.followers), 1);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map((g, idx) => {
        const pct = (g.followers / max) * 100;
        const subtitle = [g.city, g.country].filter(Boolean).join(', ');
        return (
          <Box
            key={g.id}
            onClick={() => navigate(`/dog-gardens/${g.id}`)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              borderRadius: 1.5,
              p: 0.5,
              mx: -0.5,
              '&:hover': { bgcolor: alpha(C.green, 0.06) },
            }}
          >
            <Box sx={{ width: 18, fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600, textAlign: 'center' }}>
              {idx + 1}
            </Box>
            <Avatar sx={{ width: 26, height: 26, bgcolor: alpha(C.green, 0.12), color: C.green }}>
              <ParkIcon sx={{ fontSize: 14 }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25, gap: 1 }}>
                <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
                  {g.gardenName}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ flexShrink: 0 }}>
                  {fmt(g.followers)} follower{g.followers !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    flex: 1,
                    height: 5,
                    borderRadius: 3,
                    bgcolor: alpha(C.green, 0.08),
                    '& .MuiLinearProgress-bar': { bgcolor: C.green, borderRadius: 3 },
                  }}
                />
                {subtitle && (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem', flexShrink: 0, maxWidth: 120 }} noWrap>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── User-growth line chart ──────────────────────────────────────────────────
function UserGrowthChart({ points, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (!points || points.length === 0) {
    return <Typography variant="body2" color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>No data yet.</Typography>;
  }
  // Convert ISO date string → short label "MMM DD"
  const data = points.map((p) => ({
    ...p,
    label: new Date(p.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  }));
  return (
    <Box sx={{ width: '100%', height: 240 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 10, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} allowDecimals={false} />
          <RTooltip
            contentStyle={{
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
          <Line type="monotone" dataKey="newUsers" name="New users" stroke={C.primary} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          <Line type="monotone" dataKey="newPosts" name="New posts" stroke={C.blue} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

// ── Top active users (with 3/7/30 day tabs) ────────────────────────────────
function TopActiveUsersCard() {
  const navigate = useNavigate();
  const api = useApi();
  const [days, setDays] = useState(7);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.insights.getTopActiveUsers(days, 10)
      .then((data) => { if (!cancelled) { setItems(Array.isArray(data) ? data : []); setLoading(false); } })
      .catch(() => { if (!cancelled) { setItems([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, [days, api]);

  const max = items.length ? Math.max(...items.map((i) => i.postCount)) : 1;

  return (
    <SectionCard
      title="Top Active Users"
      action={
        <Tabs
          value={days}
          onChange={(_, v) => setDays(v)}
          sx={{
            minHeight: 32,
            '& .MuiTab-root': { minHeight: 32, py: 0, px: 1.5, fontSize: '0.75rem', textTransform: 'none', fontWeight: 600 },
            '& .MuiTabs-indicator': { height: 2, bgcolor: C.primary },
          }}
        >
          <Tab label="3 days"  value={3} />
          <Tab label="7 days"  value={7} />
          <Tab label="30 days" value={30} />
        </Tabs>
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={36} />)}
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 3, textAlign: 'center' }}>
          No posts in this window yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {items.map((u, idx) => {
            const pct = (u.postCount / max) * 100;
            return (
              <Box
                key={u.userId}
                onClick={() => navigate(`/users/${u.userId}`)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 0.75,
                  px: 1,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(C.primary, 0.04) },
                }}
              >
                <Box sx={{ width: 18, fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600, textAlign: 'center' }}>
                  {idx + 1}
                </Box>
                <Avatar src={normalizeImageUrl(u.picture)} sx={{ width: 32, height: 32, bgcolor: alpha(C.primary, 0.1), color: C.primary, fontSize: '0.8rem', fontWeight: 700 }}>
                  {(u.name || u.email || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
                      {u.name || u.email || `User #${u.userId}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {fmt(u.postCount)} post{u.postCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: alpha(C.primary, 0.08),
                        '& .MuiLinearProgress-bar': { bgcolor: C.primary, borderRadius: 2 },
                      }}
                    />
                    {(u.city || u.country) && (
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
                        {[u.city, u.country].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </SectionCard>
  );
}

// ── Donut chart ─────────────────────────────────────────────────────────────
function DonutChart({ data, colorFor, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (!data || data.length === 0) {
    return <Typography variant="body2" color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>No data.</Typography>;
  }
  return (
    <Box sx={{ width: '100%', height: 200 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            dataKey="count"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={70}
            paddingAngle={2}
            strokeWidth={0}
          >
            {data.map((entry, idx) => (
              <Cell key={idx} fill={colorFor(entry.label, idx)} />
            ))}
          </Pie>
          <RTooltip
            contentStyle={{
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
              fontSize: 12,
            }}
            formatter={(value, name) => [fmt(value), name]}
          />
          <Legend
            verticalAlign="bottom"
            iconType="circle"
            wrapperStyle={{ fontSize: 11, textTransform: 'capitalize' }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

// ── Top breeds list ─────────────────────────────────────────────────────────
function TopBreedsList({ items, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={24} />)}
      </Box>
    );
  }
  if (!items || items.length === 0) {
    return <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>No breeds yet.</Typography>;
  }
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((b, idx) => (
        <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 18, fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600, textAlign: 'center' }}>
            {idx + 1}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>{b.label}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{fmt(b.count)}</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(b.count / max) * 100}
              sx={{
                mt: 0.25,
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(C.primaryLight, 0.1),
                '& .MuiLinearProgress-bar': { bgcolor: C.primaryLight, borderRadius: 2 },
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Main Dashboard ──────────────────────────────────────────────────────────
const Dashboard = () => {
  const api = useApi();
  const { currentUser } = useAuth();

  const [pendingCount, setPendingCount] = useState(0);
  const [overview, setOverview] = useState(null);
  const [topCountries, setTopCountries] = useState([]);
  const [topGardens, setTopGardens] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [dogDist, setDogDist] = useState(null);

  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingGardens, setLoadingGardens] = useState(true);
  const [loadingGrowth, setLoadingGrowth] = useState(true);
  const [loadingDogDist, setLoadingDogDist] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api.moderation.getCounts()
      .then((d) => { if (!cancelled) setPendingCount(d?.total ?? 0); })
      .catch(() => {});

    api.insights.getOverview()
      .then((d) => { if (!cancelled) { setOverview(d); setLoadingOverview(false); } })
      .catch(() => { if (!cancelled) setLoadingOverview(false); });

    api.insights.getTopCountries(10)
      .then((d) => { if (!cancelled) { setTopCountries(Array.isArray(d) ? d : []); setLoadingCountries(false); } })
      .catch(() => { if (!cancelled) setLoadingCountries(false); });

    api.insights.getTopGardens(10)
      .then((d) => { if (!cancelled) { setTopGardens(Array.isArray(d) ? d : []); setLoadingGardens(false); } })
      .catch(() => { if (!cancelled) setLoadingGardens(false); });

    api.insights.getUserGrowth(30)
      .then((d) => { if (!cancelled) { setGrowth(Array.isArray(d) ? d : []); setLoadingGrowth(false); } })
      .catch(() => { if (!cancelled) setLoadingGrowth(false); });

    api.insights.getDogDistributions()
      .then((d) => { if (!cancelled) { setDogDist(d); setLoadingDogDist(false); } })
      .catch(() => { if (!cancelled) setLoadingDogDist(false); });

    return () => { cancelled = true; };
  }, [api]);

  const firstName = currentUser?.displayName?.split(' ')[0]
    || currentUser?.email?.split('@')[0]
    || 'Admin';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const totals = overview?.totals;
  const g = overview?.growth;

  return (
    <Box>
      {/* Greeting */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          {greeting}, {firstName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Here's an overview of your Anipaw platform.
        </Typography>
      </Box>

      <PendingBanner pendingCount={pendingCount} />

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Users"
            value={totals?.users}
            delta={g?.newUsersLast7d}
            deltaLabel="this week"
            Icon={PeopleIcon}
            color={C.primary}
            loading={loadingOverview}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Dogs"
            value={totals?.dogs}
            delta={g?.newDogsLast30d}
            deltaLabel="this month"
            Icon={PetsIcon}
            color={C.primaryLight}
            loading={loadingOverview}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Posts"
            value={totals?.posts}
            delta={g?.newPostsLast7d}
            deltaLabel="this week"
            Icon={ArticleIcon}
            color={C.blue}
            loading={loadingOverview}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Providers"
            value={totals?.approvedProviders}
            delta={null}
            Icon={StoreIcon}
            color={C.amber}
            loading={loadingOverview}
          />
        </Grid>
      </Grid>

      {/* Secondary KPIs row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Active users
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>{loadingOverview ? '…' : fmt(totals?.activeUsers)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Suspended
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25, color: C.red }}>{loadingOverview ? '…' : fmt(totals?.suspendedUsers)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Visible gardens
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>{loadingOverview ? '…' : `${fmt(totals?.visibleGardens)} / ${fmt(totals?.gardens)}`}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              New posts (24h)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>{loadingOverview ? '…' : fmt(g?.newPostsLast24h)}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Countries + Gardens (side-by-side) */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Top Countries by Users">
            <TopCountriesList items={topCountries} loading={loadingCountries} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard title="Top Gardens by Followers">
            <TopGardensList items={topGardens} loading={loadingGardens} />
          </SectionCard>
        </Grid>
      </Grid>

      {/* User growth chart — full width */}
      <Box sx={{ mb: 3 }}>
        <SectionCard title="Growth — Last 30 Days">
          <UserGrowthChart points={growth} loading={loadingGrowth} />
        </SectionCard>
      </Box>

      {/* Top active users */}
      <Box sx={{ mb: 3 }}>
        <TopActiveUsersCard />
      </Box>

      {/* Dog distributions */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SectionCard title="Dogs by Energy">
            <DonutChart
              data={dogDist?.byEnergy}
              colorFor={(label) => ENERGY_COLOR[label] || C.slate}
              loading={loadingDogDist}
            />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Dogs by Size">
            <DonutChart
              data={dogDist?.bySize}
              colorFor={(label) => SIZE_COLOR[label] || C.slate}
              loading={loadingDogDist}
            />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Top 10 Breeds">
            <TopBreedsList items={dogDist?.topBreeds} loading={loadingDogDist} />
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
